import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { DocumentProps } from "@/hooks/admin/identity-docs/useSubmittedDocuments";
import { useDocumentManagement } from "@/hooks/admin/identity-docs/useDocumentManagement";
import { DocumentTypeSelector } from "../edit-dialog/DocumentTypeSelector";
import { DatePickerField } from "../edit-dialog/DatePickerField";
import { Edit, Save, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface EditableDocumentMetadataProps {
  document: DocumentProps;
  onDocumentUpdated?: () => void;
}

// Helper function to parse date string without timezone conversion
const parseDate = (dateString: string): Date => {
  // Split the date string and create a Date object with explicit values
  // This avoids timezone conversion issues
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day); // Month is 0-indexed in JavaScript
};

// Helper function to format date for display without timezone issues
const formatDateForDisplay = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC'
  });
};

export function EditableDocumentMetadata({ 
  document, 
  onDocumentUpdated 
}: EditableDocumentMetadataProps) {
  const { updateDocument, isProcessing } = useDocumentManagement();
  const [isEditing, setIsEditing] = useState(false);
  
  // Form state
  const [documentTypeId, setDocumentTypeId] = useState<string>('');
  const [documentNumber, setDocumentNumber] = useState<string>('');
  const [issueDate, setIssueDate] = useState<Date | null>(null);
  const [expiryDate, setExpiryDate] = useState<Date | null>(null);
  const [notes, setNotes] = useState<string>('');
  
  // User profile fields
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [dateOfBirth, setDateOfBirth] = useState<Date | null>(null);

  // Fetch user profile including date of birth on component mount and document changes
  useEffect(() => {
    if (document?.user_id) {
      fetchUserProfile(document.user_id);
    }
  }, [document?.user_id, document?.id]);

  // Initialize form state when entering edit mode
  useEffect(() => {
    if (isEditing && document) {
      setDocumentTypeId(document.document_type_id || '');
      setDocumentNumber(document.document_number || '');
      setIssueDate(document.issue_date ? new Date(document.issue_date) : null);
      setExpiryDate(document.expiry_date ? new Date(document.expiry_date) : null);
      setNotes(document.notes || '');
      setFirstName(document.profiles?.first_name || '');
      setLastName(document.profiles?.last_name || '');
    }
  }, [isEditing, document]);

  const fetchUserProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('date_of_birth, first_name, last_name')
        .eq('id', userId)
        .single();
      
      if (error) {
        console.error('Error fetching user profile:', error);
        return;
      }
      
      if (data?.date_of_birth) {
        // Use the parseDate helper to avoid timezone issues
        setDateOfBirth(parseDate(data.date_of_birth));
      } else {
        setDateOfBirth(null);
      }

      // Update form state if in editing mode
      if (isEditing) {
        setFirstName(data?.first_name || '');
        setLastName(data?.last_name || '');
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const handleSave = async () => {
    if (!document) return;
    
    try {
      // Prepare the update data - handle empty strings properly
      const updateData = {
        documentTypeId: documentTypeId.trim() || undefined,
        documentNumber: documentNumber.trim() === '' ? '' : documentNumber.trim() || undefined,
        issueDate: issueDate,
        expiryDate: expiryDate,
        notes: notes.trim() === '' ? '' : notes.trim() || undefined
      };

      console.log('Saving document with data:', updateData);
      
      // Update document
      const documentSuccess = await updateDocument(document.id, updateData);
      
      if (!documentSuccess) {
        toast.error('Failed to update document');
        return;
      }
      
      // Update user profile
      const profileUpdateData = {
        first_name: firstName.trim() || null,
        last_name: lastName.trim() || null,
        date_of_birth: dateOfBirth ? dateOfBirth.toISOString().split('T')[0] : null
      };

      console.log('Updating profile with data:', profileUpdateData);
      
      const { error: profileError } = await supabase
        .from('profiles')
        .update(profileUpdateData)
        .eq('id', document.user_id);
      
      if (profileError) {
        console.error('Error updating profile:', profileError);
        toast.error('Document updated but failed to update user information');
      } else {
        toast.success('Document and user information updated successfully');
      }
      
      setIsEditing(false);
      
      // Refresh the user profile data
      await fetchUserProfile(document.user_id);
      
      // Call the refresh callback
      if (onDocumentUpdated) {
        onDocumentUpdated();
      }
    } catch (error) {
      console.error('Error updating document:', error);
      toast.error('Failed to update document');
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset form state
    setDocumentTypeId(document.document_type_id || '');
    setDocumentNumber(document.document_number || '');
    setIssueDate(document.issue_date ? new Date(document.issue_date) : null);
    setExpiryDate(document.expiry_date ? new Date(document.expiry_date) : null);
    setNotes(document.notes || '');
    setFirstName(document.profiles?.first_name || '');
    setLastName(document.profiles?.last_name || '');
    
    // Refresh user profile to get latest data
    if (document.user_id) {
      fetchUserProfile(document.user_id);
    }
  };

  if (!isEditing) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium">Document Details</h4>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setIsEditing(true)}
            className="flex items-center gap-1"
          >
            <Edit className="h-3 w-3" />
            Edit Details
          </Button>
        </div>
        
        <div className="grid grid-cols-1 gap-3 text-sm">
          <div>
            <span className="font-medium">Document Type:</span>
            <p className="text-muted-foreground mt-1">
              {document.id_document_types?.document_name || 'Not specified'}
            </p>
          </div>
          
          <div>
            <span className="font-medium">Document Number:</span>
            <p className="text-muted-foreground mt-1">
              {document.document_number || 'Not provided'}
            </p>
          </div>
          
          <div>
            <span className="font-medium">Issue Date:</span>
            <p className="text-muted-foreground mt-1">
              {document.issue_date 
                ? new Date(document.issue_date).toLocaleDateString()
                : 'Not provided'
              }
            </p>
          </div>
          
          <div>
            <span className="font-medium">Expiry Date:</span>
            <p className="text-muted-foreground mt-1">
              {document.expiry_date 
                ? new Date(document.expiry_date).toLocaleDateString()
                : 'Not provided'
              }
            </p>
          </div>

          <div>
            <span className="font-medium">User Name:</span>
            <p className="text-muted-foreground mt-1">
              {document.profiles?.first_name} {document.profiles?.last_name}
            </p>
          </div>

          <div>
            <span className="font-medium">Date of Birth:</span>
            <p className="text-muted-foreground mt-1">
              {dateOfBirth 
                ? formatDateForDisplay(dateOfBirth)
                : 'Not provided'
              }
            </p>
          </div>
        </div>
        
        {document.notes && (
          <div>
            <span className="font-medium text-sm">Notes:</span>
            <p className="text-muted-foreground text-sm mt-1">{document.notes}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="border rounded-lg bg-muted/30 h-full flex flex-col">
      <div className="flex items-center justify-between p-4 border-b bg-background/50 flex-shrink-0">
        <h4 className="text-sm font-medium">Edit Document Details</h4>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleCancel}
            disabled={isProcessing}
            className="flex items-center gap-1"
          >
            <X className="h-3 w-3" />
            Cancel
          </Button>
          <Button 
            size="sm" 
            onClick={handleSave}
            disabled={isProcessing}
            className="flex items-center gap-1"
          >
            <Save className="h-3 w-3" />
            {isProcessing ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4 pb-4">
          <div>
            <DocumentTypeSelector
              value={documentTypeId}
              onValueChange={setDocumentTypeId}
            />
          </div>
          
          <div>
            <Label htmlFor="edit-document-number">Document Number</Label>
            <Input
              id="edit-document-number"
              value={documentNumber}
              onChange={(e) => setDocumentNumber(e.target.value)}
              placeholder="Enter document number"
            />
          </div>
          
          <div>
            <DatePickerField
              label="Issue Date"
              value={issueDate}
              onChange={setIssueDate}
            />
          </div>
          
          <div>
            <DatePickerField
              label="Expiry Date"
              value={expiryDate}
              onChange={setExpiryDate}
            />
          </div>

          <div className="space-y-4 border-t pt-4">
            <h5 className="text-sm font-medium">User Information</h5>
            
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="edit-first-name">First Name</Label>
                <Input
                  id="edit-first-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First name"
                />
              </div>
              
              <div>
                <Label htmlFor="edit-last-name">Last Name</Label>
                <Input
                  id="edit-last-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Last name"
                />
              </div>
            </div>

            <div>
              <DatePickerField
                label="Date of Birth"
                value={dateOfBirth}
                onChange={setDateOfBirth}
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="edit-notes">Notes</Label>
            <Textarea
              id="edit-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about this document..."
              rows={3}
            />
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}
